/**
 * Workspace Access
 *
 * Shared helpers for canonical workspace roles and workspace-membership
 * alignment when users gain Space access.
 *
 * Primary responsibilities:
 * - Resolve a user's effective workspace role
 * - Map Space roles onto the closest conservative workspace role
 * - Ensure Space access also creates workspace membership
 *
 * Dependencies:
 * - Supabase service client
 * - Space and workspace role types
 */

import type { SupabaseClient } from '@supabase/supabase-js'

import { createServiceClient } from '@/lib/supabase/service-client'
import { getBusinessPlanByInternalNexusPlan } from '@/lib/billing/losi-pricing'
import type { Database, Tables } from '@/supabase/types'
import type { SpaceRole } from '@/types/spaces'
import type { WorkspaceRole } from '@/types/workspace-invitations'

type InvitableSpaceRole = Exclude<SpaceRole, 'owner'>
type WorkspaceTeamMemberRow = Pick<Tables<'workspace_team_members'>, 'team_id'>
type WorkspaceTeamRoleRow = Pick<Tables<'workspace_teams'>, 'role'>
type WorkspaceOwnerRow = Pick<Tables<'workspaces'>, 'user_id'>
type NexusSubscriptionPlanRow = Pick<Tables<'nexus_subscriptions'>, 'plan_type'>

export const workspaceRoleRank: Record<WorkspaceRole, number> = {
  guest: 0,
  viewer: 1,
  member: 2,
  admin: 3,
  owner: 4
}

export function normalizeWorkspaceRole(
  role: string | null | undefined
): WorkspaceRole | null {
  if (
    role === 'owner' ||
    role === 'admin' ||
    role === 'member' ||
    role === 'viewer' ||
    role === 'guest'
  ) {
    return role
  }

  return null
}

function getHighestWorkspaceRole(roles: Array<WorkspaceRole | null>) {
  return roles.reduce<WorkspaceRole | null>((highest, role) => {
    if (!role) return highest
    if (!highest) return role
    return workspaceRoleRank[role] > workspaceRoleRank[highest] ? role : highest
  }, null)
}

async function getWorkspaceTeamRolesForUser(
  client: SupabaseClient<Database>,
  workspaceId: string,
  userId: string
) {
  const { data: teamMemberships, error: teamMembershipsError } = await client
    .from('workspace_team_members')
    .select('team_id')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)

  if (teamMembershipsError) {
    throw teamMembershipsError
  }

  const teamIds = Array.from(
    new Set(
      ((teamMemberships ?? []) as WorkspaceTeamMemberRow[])
        .map((team) => team.team_id)
        .filter(Boolean)
    )
  )

  if (teamIds.length === 0) {
    return []
  }

  const { data: teams, error: teamsError } = await client
    .from('workspace_teams')
    .select('role')
    .eq('workspace_id', workspaceId)
    .in('id', teamIds)

  if (teamsError) {
    throw teamsError
  }

  return ((teams ?? []) as WorkspaceTeamRoleRow[])
    .map((team) => normalizeWorkspaceRole(team.role))
    .filter((role): role is WorkspaceRole => Boolean(role))
}

// --------------

export async function workspaceHasBusinessSubscription(
  client: SupabaseClient<Database>,
  workspaceId: string
) {
  const subscriptionClient =
    typeof window === 'undefined' ? createServiceClient() : client
  const { data: workspace, error: workspaceError } = await subscriptionClient
    .from('workspaces')
    .select('user_id')
    .eq('id', workspaceId)
    .maybeSingle()

  if (workspaceError) {
    throw workspaceError
  }

  const ownerUserId = (workspace as WorkspaceOwnerRow | null)?.user_id
  if (!ownerUserId) return false

  const { data: subscription, error: subscriptionError } = await subscriptionClient
    .from('nexus_subscriptions')
    .select('plan_type')
    .eq('user_id', ownerUserId)
    .in('status', ['active', 'trialing'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (subscriptionError) {
    throw subscriptionError
  }

  return Boolean(
    getBusinessPlanByInternalNexusPlan(
      (subscription as NexusSubscriptionPlanRow | null)?.plan_type ?? null
    )
  )
}

export async function getWorkspaceRoleForUser(
  client: SupabaseClient<Database>,
  workspaceId: string,
  userId: string
): Promise<WorkspaceRole | null> {
  const { data: workspace, error: workspaceError } = await client
    .from('workspaces')
    .select('user_id')
    .eq('id', workspaceId)
    .maybeSingle()

  if (workspaceError) {
    throw workspaceError
  }

  if (workspace?.user_id === userId) {
    return 'owner'
  }

  const { data: workspaceMember, error: workspaceMemberError } = await client
    .from('workspace_members')
    .select('role')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .maybeSingle()

  if (workspaceMemberError) {
    throw workspaceMemberError
  }

  const directRole = normalizeWorkspaceRole(workspaceMember?.role)
  if (!directRole || directRole === 'guest') {
    return directRole
  }

  const teamRoles = await getWorkspaceTeamRolesForUser(client, workspaceId, userId)
  return getHighestWorkspaceRole([directRole, ...teamRoles])
}

export function mapSpaceRoleToWorkspaceRole(
  role: InvitableSpaceRole
): WorkspaceRole {
  if (role === 'admin' || role === 'editor') {
    return 'member'
  }

  return 'guest'
}

export function getHigherWorkspaceRole(
  first: WorkspaceRole | null,
  second: WorkspaceRole
): WorkspaceRole {
  if (!first) {
    return second
  }

  return workspaceRoleRank[first] >= workspaceRoleRank[second]
    ? first
    : second
}

type EnsureWorkspaceMemberInput = {
  workspaceId: string
  userId: string
  spaceRole: InvitableSpaceRole
  addedBy: string
  client?: SupabaseClient<Database>
}

export async function ensureWorkspaceMember({
  workspaceId,
  userId,
  spaceRole,
  addedBy,
  client
}: EnsureWorkspaceMemberInput): Promise<WorkspaceRole> {
  const supabase = client ?? createServiceClient()
  const existingRole = await getWorkspaceRoleForUser(
    supabase,
    workspaceId,
    userId
  )

  if (existingRole === 'owner') {
    return existingRole
  }

  const nextRole = mapSpaceRoleToWorkspaceRole(spaceRole)

  if (!existingRole) {
    const { error: insertError } = await supabase
      .from('workspace_members')
      .insert({
        workspace_id: workspaceId,
        user_id: userId,
        role: nextRole,
        added_by: addedBy
      })

    if (insertError && insertError.code !== '23505') {
      throw insertError
    }

    if (insertError?.code === '23505') {
      return (
        (await getWorkspaceRoleForUser(supabase, workspaceId, userId)) ||
        nextRole
      )
    }

    return nextRole
  }

  if (workspaceRoleRank[existingRole] >= workspaceRoleRank[nextRole]) {
    return existingRole
  }

  const { error: updateError } = await supabase
    .from('workspace_members')
    .update({ role: nextRole })
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)

  if (updateError) {
    throw updateError
  }

  return nextRole
}
